import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext(null);

const translations = {
  fr: {
    // Navigation
    nav: {
      home: "Accueil",
      about: "À Propos",
      portfolio: "Portfolio",
      contact: "Contact",
      clientArea: "Espace Client",
      login: "Connexion",
      logout: "Déconnexion",
      dashboard: "Tableau de Bord"
    },
    // Hero
    hero: {
      tagline: "INGÉNIERIE IMMOBILIÈRE DE PRESTIGE",
      title: "Moon River",
      subtitle: "Real Estate",
      description: "Excellence et discrétion au service de vos investissements immobiliers d'exception. Accédez à des opportunités off-market exclusives.",
      cta: "Découvrir nos opportunités",
      ctaSecondary: "Prendre rendez-vous"
    },
    // About
    about: {
      label: "NOTRE EXPERTISE",
      title: "L'Art de l'Ingénierie Immobilière",
      description: "Moon River Real Estate incarne l'excellence dans l'accompagnement des investisseurs exigeants. Notre approche sur-mesure allie expertise technique, connaissance approfondie des marchés et discrétion absolue.",
      stats: {
        experience: "Années d'expertise",
        transactions: "M€ de transactions",
        clients: "Clients accompagnés",
        properties: "Biens exclusifs"
      },
      services: {
        title: "Nos Services",
        advisory: {
          title: "Conseil Stratégique",
          description: "Analyse approfondie et recommandations personnalisées pour optimiser votre patrimoine immobilier."
        },
        sourcing: {
          title: "Sourcing Off-Market",
          description: "Accès privilégié à des opportunités exclusives avant leur mise sur le marché."
        },
        management: {
          title: "Gestion Patrimoniale",
          description: "Suivi rigoureux et valorisation continue de vos actifs immobiliers."
        }
      }
    },
    // Portfolio
    portfolio: {
      label: "OPPORTUNITÉS EXCLUSIVES",
      title: "Portfolio Off-Market",
      description: "Découvrez notre sélection confidentielle de biens d'exception réservés à nos clients.",
      authRequired: "Authentification requise",
      loginToView: "Connectez-vous pour accéder aux détails",
      viewDetails: "Voir les détails",
      bedrooms: "Chambres",
      bathrooms: "Salles de bain",
      surface: "Surface"
    },
    // Contact
    contact: {
      label: "QUALIFICATION",
      title: "Planifiez votre Appel Stratégique",
      description: "Partagez vos objectifs d'investissement pour un accompagnement sur-mesure.",
      form: {
        name: "Nom complet",
        email: "Email",
        phone: "Téléphone",
        budget: "Budget d'investissement",
        budgetOptions: {
          select: "Sélectionnez votre budget",
          under500: "Moins de 500 000 €",
          "500to1m": "500 000 € - 1 000 000 €",
          "1mto3m": "1 000 000 € - 3 000 000 €",
          "3mto5m": "3 000 000 € - 5 000 000 €",
          over5m: "Plus de 5 000 000 €"
        },
        investmentType: "Type d'investissement",
        investmentOptions: {
          select: "Sélectionnez le type",
          residential: "Résidentiel de prestige",
          commercial: "Commercial haut de gamme",
          mixed: "Mixte",
          land: "Foncier",
          development: "Promotion immobilière"
        },
        horizon: "Horizon de placement",
        horizonOptions: {
          select: "Sélectionnez l'horizon",
          short: "Court terme (< 2 ans)",
          medium: "Moyen terme (2-5 ans)",
          long: "Long terme (> 5 ans)"
        },
        message: "Message (optionnel)",
        submit: "Envoyer ma demande"
      }
    },
    // Appointment
    appointment: {
      label: "RENDEZ-VOUS",
      title: "Réservez votre créneau",
      selectDate: "Sélectionnez une date",
      selectTime: "Horaire disponible",
      subject: "Sujet de l'appel",
      subjectOptions: {
        select: "Sélectionnez un sujet",
        discovery: "Appel découverte",
        investment: "Projet d'investissement",
        followup: "Suivi de dossier",
        other: "Autre"
      },
      notes: "Notes additionnelles",
      confirm: "Confirmer le rendez-vous"
    },
    // Dashboard
    dashboard: {
      welcome: "Bienvenue",
      overview: "Vue d'ensemble",
      myAppointments: "Mes Rendez-vous",
      savedProperties: "Biens Sauvegardés",
      stats: {
        totalLeads: "Leads totaux",
        newLeads: "Nouveaux leads",
        appointments: "Rendez-vous",
        upcoming: "À venir",
        properties: "Biens"
      }
    },
    // Auth
    auth: {
      login: {
        title: "Connexion",
        subtitle: "Accédez à votre espace client",
        email: "Email",
        password: "Mot de passe",
        submit: "Se connecter",
        noAccount: "Pas encore de compte ?",
        register: "Créer un compte",
        orContinueWith: "Ou continuer avec"
      },
      register: {
        title: "Créer un compte",
        subtitle: "Rejoignez Moon River Real Estate",
        name: "Nom complet",
        email: "Email",
        phone: "Téléphone",
        password: "Mot de passe",
        confirmPassword: "Confirmer le mot de passe",
        submit: "Créer mon compte",
        hasAccount: "Déjà un compte ?",
        login: "Se connecter"
      }
    },
    // Footer
    footer: {
      description: "Excellence et discrétion au service de vos investissements immobiliers d'exception.",
      quickLinks: "Liens rapides",
      contact: "Contact",
      legal: "Mentions légales",
      privacy: "Politique de confidentialité",
      rights: "Tous droits réservés"
    },
    // Common
    common: {
      loading: "Chargement...",
      error: "Une erreur est survenue",
      success: "Succès",
      cancel: "Annuler",
      save: "Enregistrer",
      edit: "Modifier",
      delete: "Supprimer",
      view: "Voir",
      back: "Retour"
    }
  },
  en: {
    // Navigation
    nav: {
      home: "Home",
      about: "About",
      portfolio: "Portfolio",
      contact: "Contact",
      clientArea: "Client Area",
      login: "Login",
      logout: "Logout",
      dashboard: "Dashboard"
    },
    // Hero
    hero: {
      tagline: "PREMIUM REAL ESTATE ENGINEERING",
      title: "Moon River",
      subtitle: "Real Estate",
      description: "Excellence and discretion at the service of your exceptional real estate investments. Access exclusive off-market opportunities.",
      cta: "Discover our opportunities",
      ctaSecondary: "Book an appointment"
    },
    // About
    about: {
      label: "OUR EXPERTISE",
      title: "The Art of Real Estate Engineering",
      description: "Moon River Real Estate embodies excellence in supporting discerning investors. Our bespoke approach combines technical expertise, in-depth market knowledge, and absolute discretion.",
      stats: {
        experience: "Years of expertise",
        transactions: "M€ in transactions",
        clients: "Clients served",
        properties: "Exclusive properties"
      },
      services: {
        title: "Our Services",
        advisory: {
          title: "Strategic Advisory",
          description: "In-depth analysis and personalized recommendations to optimize your real estate portfolio."
        },
        sourcing: {
          title: "Off-Market Sourcing",
          description: "Privileged access to exclusive opportunities before they hit the market."
        },
        management: {
          title: "Asset Management",
          description: "Rigorous monitoring and continuous enhancement of your real estate assets."
        }
      }
    },
    // Portfolio
    portfolio: {
      label: "EXCLUSIVE OPPORTUNITIES",
      title: "Off-Market Portfolio",
      description: "Discover our confidential selection of exceptional properties reserved for our clients.",
      authRequired: "Authentication required",
      loginToView: "Log in to access details",
      viewDetails: "View details",
      bedrooms: "Bedrooms",
      bathrooms: "Bathrooms",
      surface: "Surface"
    },
    // Contact
    contact: {
      label: "QUALIFICATION",
      title: "Schedule your Strategic Call",
      description: "Share your investment goals for tailored support.",
      form: {
        name: "Full name",
        email: "Email",
        phone: "Phone",
        budget: "Investment budget",
        budgetOptions: {
          select: "Select your budget",
          under500: "Under €500,000",
          "500to1m": "€500,000 - €1,000,000",
          "1mto3m": "€1,000,000 - €3,000,000",
          "3mto5m": "€3,000,000 - €5,000,000",
          over5m: "Over €5,000,000"
        },
        investmentType: "Investment type",
        investmentOptions: {
          select: "Select type",
          residential: "Premium residential",
          commercial: "High-end commercial",
          mixed: "Mixed-use",
          land: "Land",
          development: "Real estate development"
        },
        horizon: "Investment horizon",
        horizonOptions: {
          select: "Select horizon",
          short: "Short term (< 2 years)",
          medium: "Medium term (2-5 years)",
          long: "Long term (> 5 years)"
        },
        message: "Message (optional)",
        submit: "Submit my request"
      }
    },
    // Appointment
    appointment: {
      label: "APPOINTMENT",
      title: "Book your slot",
      selectDate: "Select a date",
      selectTime: "Available time",
      subject: "Call subject",
      subjectOptions: {
        select: "Select a subject",
        discovery: "Discovery call",
        investment: "Investment project",
        followup: "Case follow-up",
        other: "Other"
      },
      notes: "Additional notes",
      confirm: "Confirm appointment"
    },
    // Dashboard
    dashboard: {
      welcome: "Welcome",
      overview: "Overview",
      myAppointments: "My Appointments",
      savedProperties: "Saved Properties",
      stats: {
        totalLeads: "Total leads",
        newLeads: "New leads",
        appointments: "Appointments",
        upcoming: "Upcoming",
        properties: "Properties"
      }
    },
    // Auth
    auth: {
      login: {
        title: "Login",
        subtitle: "Access your client area",
        email: "Email",
        password: "Password",
        submit: "Sign in",
        noAccount: "Don't have an account?",
        register: "Create account",
        orContinueWith: "Or continue with"
      },
      register: {
        title: "Create account",
        subtitle: "Join Moon River Real Estate",
        name: "Full name",
        email: "Email",
        phone: "Phone",
        password: "Password",
        confirmPassword: "Confirm password",
        submit: "Create my account",
        hasAccount: "Already have an account?",
        login: "Sign in"
      }
    },
    // Footer
    footer: {
      description: "Excellence and discretion at the service of your exceptional real estate investments.",
      quickLinks: "Quick links",
      contact: "Contact",
      legal: "Legal notice",
      privacy: "Privacy policy",
      rights: "All rights reserved"
    },
    // Common
    common: {
      loading: "Loading...",
      error: "An error occurred",
      success: "Success",
      cancel: "Cancel",
      save: "Save",
      edit: "Edit",
      delete: "Delete",
      view: "View",
      back: "Back"
    }
  },
  pt: {
    nav: {
      home: "Início",
      about: "Sobre",
      portfolio: "Portfólio",
      contact: "Contacto",
      clientArea: "Área do Cliente",
      login: "Entrar",
      logout: "Sair",
      dashboard: "Painel de Controlo"
    },
    hero: {
      tagline: "ENGENHARIA IMOBILIÁRIA DE PRESTÍGIO",
      title: "Moon River",
      subtitle: "Real Estate",
      description: "Excelência e discrição ao serviço dos seus investimentos imobiliários de exceção. Aceda a oportunidades off-market exclusivas.",
      cta: "Descubra as nossas oportunidades",
      ctaSecondary: "Marcar consulta"
    },
    about: {
      label: "A NOSSA EXPERTISE",
      title: "A Arte da Engenharia Imobiliária",
      description: "A Moon River Real Estate incorpora a excelência no acompanhamento de investidores exigentes. A nossa abordagem personalizada combina expertise técnica, conhecimento profundo dos mercados e discrição absoluta.",
      stats: {
        experience: "Anos de experiência",
        transactions: "M€ em transações",
        clients: "Clientes acompanhados",
        properties: "Imóveis exclusivos"
      },
      services: {
        title: "Os Nossos Serviços",
        advisory: {
          title: "Consultoria Estratégica",
          description: "Análise aprofundada e recomendações personalizadas para otimizar o seu património imobiliário."
        },
        sourcing: {
          title: "Sourcing Off-Market",
          description: "Acesso privilegiado a oportunidades exclusivas antes da sua colocação no mercado."
        },
        management: {
          title: "Gestão Patrimonial",
          description: "Acompanhamento rigoroso e valorização contínua dos seus ativos imobiliários."
        }
      }
    },
    portfolio: {
      label: "OPORTUNIDADES EXCLUSIVAS",
      title: "Portfólio Off-Market",
      description: "Descubra a nossa seleção confidencial de imóveis de exceção reservados aos nossos clientes.",
      authRequired: "Autenticação necessária",
      loginToView: "Inicie sessão para aceder aos detalhes",
      viewDetails: "Ver detalhes",
      bedrooms: "Quartos",
      bathrooms: "Casas de banho",
      surface: "Superfície"
    },
    contact: {
      label: "QUALIFICAÇÃO",
      title: "Agende a sua Chamada Estratégica",
      description: "Partilhe os seus objetivos de investimento para um acompanhamento personalizado.",
      form: {
        name: "Nome completo",
        email: "Email",
        phone: "Telefone",
        budget: "Orçamento de investimento",
        budgetOptions: {
          select: "Selecione o seu orçamento",
          under500: "Menos de 500 000 €",
          "500to1m": "500 000 € - 1 000 000 €",
          "1mto3m": "1 000 000 € - 3 000 000 €",
          "3mto5m": "3 000 000 € - 5 000 000 €",
          over5m: "Mais de 5 000 000 €"
        },
        investmentType: "Tipo de investimento",
        investmentOptions: {
          select: "Selecione o tipo",
          residential: "Residencial de prestígio",
          commercial: "Comercial de gama alta",
          mixed: "Misto",
          land: "Fundiário",
          development: "Promoção imobiliária"
        },
        horizon: "Horizonte de investimento",
        horizonOptions: {
          select: "Selecione o horizonte",
          short: "Curto prazo (< 2 anos)",
          medium: "Médio prazo (2-5 anos)",
          long: "Longo prazo (> 5 anos)"
        },
        message: "Mensagem (opcional)",
        submit: "Enviar o meu pedido"
      }
    },
    appointment: {
      label: "CONSULTA",
      title: "Reserve o seu horário",
      selectDate: "Selecione uma data",
      selectTime: "Horário disponível",
      subject: "Assunto da chamada",
      subjectOptions: {
        select: "Selecione um assunto",
        discovery: "Chamada de descoberta",
        investment: "Projeto de investimento",
        followup: "Acompanhamento de dossier",
        other: "Outro"
      },
      notes: "Notas adicionais",
      confirm: "Confirmar consulta"
    },
    dashboard: {
      welcome: "Bem-vindo",
      overview: "Visão geral",
      myAppointments: "As Minhas Consultas",
      savedProperties: "Imóveis Guardados",
      stats: {
        totalLeads: "Total de leads",
        newLeads: "Novos leads",
        appointments: "Consultas",
        upcoming: "Próximas",
        properties: "Imóveis"
      }
    },
    auth: {
      login: {
        title: "Entrar",
        subtitle: "Aceda à sua área de cliente",
        email: "Email",
        password: "Palavra-passe",
        submit: "Iniciar sessão",
        noAccount: "Ainda não tem conta?",
        register: "Criar conta",
        orContinueWith: "Ou continuar com"
      },
      register: {
        title: "Criar conta",
        subtitle: "Junte-se à Moon River Real Estate",
        name: "Nome completo",
        email: "Email",
        phone: "Telefone",
        password: "Palavra-passe",
        confirmPassword: "Confirmar palavra-passe",
        submit: "Criar a minha conta",
        hasAccount: "Já tem conta?",
        login: "Iniciar sessão"
      }
    },
    footer: {
      description: "Excelência e discrição ao serviço dos seus investimentos imobiliários de exceção.",
      quickLinks: "Links rápidos",
      contact: "Contacto",
      legal: "Avisos legais",
      privacy: "Política de privacidade",
      rights: "Todos os direitos reservados"
    },
    common: {
      loading: "A carregar...",
      error: "Ocorreu um erro",
      success: "Sucesso",
      cancel: "Cancelar",
      save: "Guardar",
      edit: "Editar",
      delete: "Eliminar",
      view: "Ver",
      back: "Voltar"
    }
  }
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('moonriver_language');
    return saved || 'fr';
  });

  useEffect(() => {
    localStorage.setItem('moonriver_language', language);
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'fr' ? 'en' : prev === 'en' ? 'pt' : 'fr');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
